/**
 * Animation retargeting for Three.js
 * From: https://github.com/upf-gti/retargeting-threejs
 * License: Apache-2.0
 */

import * as THREE from 'three';

// asymetric and/or negative scaling of objects is not properly supported
class AnimationRetargeting {
    static BindPoseModes = { DEFAULT : 0, CURRENT: 1}

    constructor(srcSkeleton, trgSkeleton, options = {}){
        this.srcSkeleton = srcSkeleton;
        if (!srcSkeleton.boneInverses){
            srcSkeleton.traverse((o) => { if(o.isSkinnedMesh){ this.srcSkeleton = o.skeleton; } });
        }
        this.trgSkeleton = trgSkeleton;
        if (!trgSkeleton.boneInverses){
            trgSkeleton.traverse((o) => { if(o.isSkinnedMesh){ this.trgSkeleton = o.skeleton; } });
        }
        this.boneMap = this.computeBoneMap(this.srcSkeleton, this.trgSkeleton, options.boneNameMap);
        this.srcBindPose = this.cloneRawSkeleton(this.srcSkeleton, options.srcPoseMode, options.srcEmbedWorldTransforms);
        this.trgBindPose = this.cloneRawSkeleton(this.trgSkeleton, options.trgPoseMode, options.trgEmbedWorldTransforms);
        this.precomputedQuats = this._precomputeRetargetingQuats();
        this.proportionRatio = this._computeProportionRatio();
    }

    _newTransform(){
        return { p: new THREE.Vector3(0,0,0), q: new THREE.Quaternion(0,0,0,1), s: new THREE.Vector3(1,1,1) };
    }

    cloneRawSkeleton(skeleton, poseMode, embedWorld = false){
        let bones = skeleton.bones;
        let resultBones = new Array(bones.length);
        let parentIndices = new Int16Array(bones.length);

        for(let i = 0; i < bones.length; ++i){
            resultBones[i] = bones[i].clone(false);
            resultBones[i].parent = null;
        }

        for(let i = 0; i < bones.length; ++i){
            let parentIdx = findIndexOfBone(skeleton, bones[i].parent)
            if (parentIdx > -1){ resultBones[parentIdx].add(resultBones[i]); }
            parentIndices[i] = parentIdx;
        }

        // Update world matrices starting from every root bone (bones with no parent in skeleton).
        // Cannot assume bones[0] is the root — FBXLoader may order bones arbitrarily.
        for(let i = 0; i < parentIndices.length; ++i){
            if (parentIndices[i] < 0){ resultBones[i].updateWorldMatrix(false, true); }
        }

        let resultSkeleton;
        switch(poseMode) {
            case AnimationRetargeting.BindPoseModes.CURRENT:
                resultSkeleton = new THREE.Skeleton(resultBones);
                break;
            default: {
                let boneInverses = new Array(skeleton.boneInverses.length);
                for(let i = 0; i < boneInverses.length; ++i){
                    boneInverses[i] = skeleton.boneInverses[i].clone();
                }
                resultSkeleton = new THREE.Skeleton(resultBones, boneInverses);
                resultSkeleton.pose();
                break;
            }
        }

        resultSkeleton.parentIndices = parentIndices;

        let transforms = new Array(skeleton.bones.length);
        let transformsInverses = new Array(skeleton.bones.length);
        for(let i = 0; i < transforms.length; ++i){
            let t = this._newTransform();
            resultSkeleton.bones[i].matrixWorld.decompose(t.p, t.q, t.s);
            transforms[i] = t;

            t = this._newTransform();
            resultSkeleton.boneInverses[i].decompose(t.p, t.q, t.s);
            transformsInverses[i] = t;
        }
        resultSkeleton.transformsWorld = transforms;
        resultSkeleton.transformsWorldInverses = transformsInverses;

        // Find actual root bone (first bone with no parent in skeleton)
        let rootBoneIdx = 0;
        for(let i = 0; i < parentIndices.length; ++i){
            if (parentIndices[i] < 0){ rootBoneIdx = i; break; }
        }

        if (embedWorld && bones[rootBoneIdx].parent){
            let embedded = { forward: this._newTransform(), inverse: this._newTransform() };
            let t = embedded.forward;
            bones[rootBoneIdx].parent.updateWorldMatrix(true, false);
            bones[rootBoneIdx].parent.matrixWorld.decompose(t.p, t.q, t.s);
            t = embedded.inverse;
            skeleton.bones[rootBoneIdx].parent.matrixWorld.clone().invert().decompose(t.p, t.q, t.s);
            resultSkeleton.transformsWorldEmbedded = embedded;
        }
        return resultSkeleton;
    }

    computeBoneMap(srcSkeleton, trgSkeleton, boneMap = null){
        let srcBones = srcSkeleton.bones;
        let result = {
            idxMap: new Int16Array(srcBones.length),
            nameMap: {}
        }
        result.idxMap.fill(-1);
        if (boneMap) {
            for (let srcName in boneMap){
                let idx = findIndexOfBoneByName(srcSkeleton, srcName);
                if (idx < 0){ continue; }
                let trgIdx = findIndexOfBoneByName(trgSkeleton, boneMap[srcName]);
                result.idxMap[idx] = trgIdx;
                result.nameMap[srcName] = boneMap[srcName];
            }
        }
        return result
    }

    _computeProportionRatio(){
        let srcLength = 0;
        for(let i = 1; i < this.srcBindPose.bones.length; i++) {
            if (!this.srcBindPose.bones[i].parent) continue;
            let dist = this.srcBindPose.bones[i].getWorldPosition(new THREE.Vector3()).distanceTo(this.srcBindPose.bones[i].parent.getWorldPosition(new THREE.Vector3()))
            srcLength += dist;
        }

        let trgLength = 0;
        for(let i = 1; i < this.trgBindPose.bones.length; i++) {
            if (!this.trgBindPose.bones[i].parent) continue;
            let dist = this.trgBindPose.bones[i].getWorldPosition(new THREE.Vector3()).distanceTo(this.trgBindPose.bones[i].parent.getWorldPosition(new THREE.Vector3()))
            trgLength += dist;
        }

        if (srcLength === 0) {
            return 1.0;
        }
        return trgLength / srcLength
    }

    _precomputeRetargetingQuats(){
        let left = new Array(this.srcBindPose.bones.length);
        let right = new Array(this.srcBindPose.bones.length);

        for(let srcIndex = 0; srcIndex < left.length; ++srcIndex){
            let trgIndex = this.boneMap.idxMap[srcIndex];
            if(trgIndex < 0){
                left[srcIndex] = null;
                right[srcIndex] = null;
                continue;
            }

            let resultQuat = new THREE.Quaternion(0,0,0,1);
            resultQuat.copy(this.trgBindPose.transformsWorld[trgIndex].q);
            if (this.trgBindPose.transformsWorldEmbedded){ resultQuat.premultiply(this.trgBindPose.transformsWorldEmbedded.forward.q); }
            if (this.srcBindPose.transformsWorldEmbedded){ resultQuat.premultiply(this.srcBindPose.transformsWorldEmbedded.inverse.q); }
            resultQuat.premultiply(this.srcBindPose.transformsWorldInverses[srcIndex].q);
            right[srcIndex] = resultQuat;

            resultQuat = new THREE.Quaternion(0,0,0,1);
            if (this.srcBindPose.bones[srcIndex].parent){
                let parentIdx = this.srcBindPose.parentIndices[srcIndex];
                resultQuat.premultiply(this.srcBindPose.transformsWorld[parentIdx].q);
            }

            if (this.srcBindPose.transformsWorldEmbedded){ resultQuat.premultiply(this.srcBindPose.transformsWorldEmbedded.forward.q); }
            if (this.trgBindPose.transformsWorldEmbedded){ resultQuat.premultiply(this.trgBindPose.transformsWorldEmbedded.inverse.q); }

            if (this.trgBindPose.bones[trgIndex].parent){
                let parentIdx = this.trgBindPose.parentIndices[trgIndex];
                resultQuat.premultiply(this.trgBindPose.transformsWorldInverses[parentIdx].q);
            }
            left[srcIndex] = resultQuat
        }

        return { left: left, right: right };
    }

    _retargetQuaternion(srcIndex, srcLocalQuat, resultQuat = null){
        if (!resultQuat){ resultQuat = new THREE.Quaternion(0,0,0,1); }

        resultQuat.copy(srcLocalQuat);
        resultQuat.premultiply(this.precomputedQuats.left[srcIndex]);
        resultQuat.multiply(this.precomputedQuats.right[srcIndex]);
        return resultQuat;
    }

    retargetPositionTrack(srcTrack){
        let boneName = srcTrack.name.slice(0, srcTrack.name.length - 9);
        const boneIndex = findIndexOfBoneByName(this.srcSkeleton, boneName);
        if (boneIndex < 0 || this.boneMap.idxMap[boneIndex] < 0){
            return null;
        }
        const srcValues = srcTrack.values;
        let trgValues = new Float32Array(srcValues.length);
        if(boneIndex == 0) {
            let trgBoneIndex = this.boneMap.idxMap[boneIndex];

            let srcBindPos = this.srcBindPose.bones[boneIndex].getWorldPosition(new THREE.Vector3());
            let trgBindPos = this.trgBindPose.bones[trgBoneIndex].getWorldPosition(new THREE.Vector3());

            let pos = new THREE.Vector3();
            let diffPosition = new THREE.Vector3();

            for(let i = 0; i < srcValues.length; i+=3){

                pos.set(srcValues[i], srcValues[i+1], srcValues[i+2]);
                if(this.srcBindPose.transformsWorldEmbedded) {
                    pos.applyQuaternion(this.srcBindPose.transformsWorldEmbedded.forward.q);
                }
                diffPosition.subVectors(pos, srcBindPos);

                diffPosition.multiplyScalar(this.proportionRatio);

                if(this.trgBindPose.transformsWorldEmbedded) {
                    diffPosition.applyQuaternion(this.trgBindPose.transformsWorldEmbedded.inverse.q);
                }
                diffPosition.add(trgBindPos);

                trgValues[i] = diffPosition.x;
                trgValues[i+1] = diffPosition.y;
                trgValues[i+2] = diffPosition.z;
            }
        }
        return new THREE.VectorKeyframeTrack(this.boneMap.nameMap[boneName] + ".position", srcTrack.times.slice(), trgValues);
    }

    retargetQuaternionTrack(srcTrack){
        let boneName = srcTrack.name.slice(0, srcTrack.name.length - 11);
        const boneIndex = findIndexOfBoneByName(this.srcSkeleton, boneName);
        if (boneIndex < 0 || this.boneMap.idxMap[boneIndex] < 0){
            return null;
        }

        let quat = new THREE.Quaternion(0,0,0,1);
        const srcValues = srcTrack.values;
        let trgValues = new Float32Array(srcValues.length);
        for(let i = 0; i < srcValues.length; i+=4){
            quat.set(srcValues[i], srcValues[i+1], srcValues[i+2], srcValues[i+3]);
            this._retargetQuaternion(boneIndex, quat, quat);
            trgValues[i] = quat.x;
            trgValues[i+1] = quat.y;
            trgValues[i+2] = quat.z;
            trgValues[i+3] = quat.w;
        }

        return new THREE.QuaternionKeyframeTrack(this.boneMap.nameMap[boneName] + ".quaternion", srcTrack.times.slice(), trgValues);
    }

    retargetAnimation(anim){
        let trgTracks = [];
        let srcTracks = anim.tracks;
        for(let i = 0; i < srcTracks.length; ++i){
            let t = srcTracks[i];
            let newTrack = null;
            if (t.name.endsWith(".position") && t.name.includes(this.srcSkeleton.bones[0].name)){ newTrack = this.retargetPositionTrack(t); }
            else if (t.name.endsWith(".quaternion")){ newTrack = this.retargetQuaternionTrack(t); }

            if (newTrack){ trgTracks.push(newTrack); }
        }
        return new THREE.AnimationClip(anim.name, -1, trgTracks, anim.blendMode);
    }
}

function findIndexOfBone(skeleton, bone){
    if (!bone){ return -1;}
    let b = skeleton.bones;
    for(let i = 0; i < b.length; ++i){
        if (b[i] == bone){ return i; }
    }
    return -1;
}

function findIndexOfBoneByName(skeleton, name){
    if (!name){ return -1; }
    let b = skeleton.bones;
    for(let i = 0; i < b.length; ++i){
        if (b[i].name == name){ return i; }
    }
    return -1;
}

export { AnimationRetargeting, findIndexOfBone, findIndexOfBoneByName };
